import { Destroyer, filterNullish } from '@adaptiva/core/common';
import { Licensing } from '@adaptiva/core/licensing';
import { IClassLazyLoader, ILazyLoadedRegistration, LAZY_LOADED_CLASSES_TOKEN, LAZY_LOADED_REGISTRATIONS_TOKEN, LoaderModule } from '@adaptiva/core/loaders';
import { LoggingService } from '@adaptiva/core/logging';
import { EAuthState, LicenseManagerService } from '@adaptiva/data-service';
import { effect, EffectRef, inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { BehaviorSubject, catchError, distinctUntilChanged, filter, firstValueFrom, Observable, of, Subscription, switchMap, zip } from 'rxjs';

/**
 * @author Jesse Rogers <jesse.rogers@adaptiva.com>
 * @description Singleton service to manage lazy loading class definitions for object summaries,
 * 	menu providers, tree providers, etc. based on product licensing state
 */
@Injectable({
	providedIn: 'root'
})
export class LazyLoadedClassService extends Destroyer {

	public readonly isLoading: BehaviorSubject<boolean> = new BehaviorSubject(true);

	protected readonly alreadyLoaded: Set<IClassLazyLoader> = new Set();

	protected readonly injector: Injector = inject(Injector);

	protected readonly licenseManager: LicenseManagerService = inject(LicenseManagerService);

	protected readonly log: LoggingService = inject(LoggingService);

	protected readonly lazyLoaders: IClassLazyLoader[] = inject(LAZY_LOADED_CLASSES_TOKEN);

	protected readonly lazyRegistrations: ILazyLoadedRegistration[] = inject(LAZY_LOADED_REGISTRATIONS_TOKEN);

	protected readonly integrationFx: EffectRef = effect(() => {
		zip(this.lazyRegistrations.map(r => runInInjectionContext(this.injector, r.trigger))).pipe(
			this.takeOneUntilDestroyed(),
		).subscribe({
			next: (_triggers: boolean[]) => {
				_triggers.forEach(async (_shouldLoad: boolean, i: int) => {
					if (_shouldLoad)
						await this.loadIfLicensed(this.lazyRegistrations[i]);
				});
			}
		});
	});

	protected licenseSubscription!: Subscription;

	public load(): void {
		if (this.licenseSubscription)
			return;

		this.licenseSubscription = this.licenseManager.authentication.authState.pipe(
			filter(((_state: EAuthState) => _state === EAuthState.AUTHENTICATED)),
			distinctUntilChanged(),
			switchMap(() => this.licenseManager.licenseStates),
			filterNullish(),
			switchMap(async (_licenseStates: Map<string, Licensing.States>) => {
				this.isLoading.next(true);

				for (const [_product, _state] of _licenseStates) {
					if (
						_state === Licensing.States.NOT_PRESENT ||
						_state === Licensing.States.REMOVING
					)
						continue;

					for (const _loader of this.lazyLoaders) {
						if (_loader.product === _product || _loader.product === '*')
							await this.loadIfLicensed(_loader);
					}
				}
			}),
			catchError(this.handleLoaderError.bind(this)),
			this.takeUntilDestroyed(),
		).subscribe({
			next: () => {
				this.isLoading.next(false);
			}
		});
	}

	protected async loadIfLicensed(_loader: IClassLazyLoader): Promise<void> {
		if (!this.alreadyLoaded.has(_loader)) {
			try {
				const _module: typeof LoaderModule = await runInInjectionContext(this.injector, () => _loader.load() as Promise<typeof LoaderModule>);

				this.alreadyLoaded.add(_loader);
				this.log.debug(
					'LazyLoadedClassService.loadIfLicensed',
					'Successfully lazy-loaded class modules for ' +
					this.licenseManager.getUserFriendlyProductName(_loader.product)
				)();

				if (_module.onLoad)
					await firstValueFrom(runInInjectionContext(this.injector, () => _module.onLoad()));
			} catch (e) {
				return firstValueFrom(this.handleLoaderError(_loader, e));
			}
		}
	}

	protected handleLoaderError(_loader: IClassLazyLoader, e: Error): Observable<void> {
		this.isLoading.next(false);
		this.log.error(
			'LazyLoadedClassService.handleLoaderError',
			'Failed to lazy-load class modules for ' +
			this.licenseManager.getUserFriendlyProductName(_loader.product),
			this.log.extractErrorMessage(e)
		)();

		return of(void 0);
	}

}
